import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { contactoService, Contacto, TIPO_CONTACTO_OPTIONS } from '../services/contactoService';
import DataTable from '../components/DataTable';
import Notification from '../components/Notification';
import { useClienteSucursalFilter } from '../hooks/useClienteSucursalFilter';

const ContactosPage: React.FC = () => {
  const navigate = useNavigate();
  const [contactos, setContactos] = useState<Contacto[]>([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);
  const [clienteId, setClienteId] = useState<number | ''>('');
  const [sucursalId, setSucursalId] = useState<number | ''>('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const { clienteOptions, sucursalOptions, loadSucursalesForCliente } = useClienteSucursalFilter();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await contactoService.getAll({
        clienteId: clienteId === '' ? null : clienteId,
        sucursalId: sucursalId === '' ? null : sucursalId,
        fechaDesde: fechaDesde || null,
        fechaHasta: fechaHasta || null,
      });
      setContactos(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setNotification({
        type: 'error',
        message: e?.message || 'Error al cargar contactos',
      });
      setContactos([]);
    } finally {
      setLoading(false);
    }
  }, [clienteId, sucursalId, fechaDesde, fechaHasta]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (row: Contacto) => {
    if (!window.confirm(`¿Eliminar el contacto #${row.id}?`)) return;
    try {
      await contactoService.remove(row.id);
      setNotification({ type: 'success', message: 'Contacto eliminado' });
      await load();
    } catch (e: any) {
      setNotification({
        type: 'error',
        message: e?.message || 'Error al eliminar',
      });
    }
  };

  const columns = [
    {
      key: 'fecha',
      label: 'Fecha',
      render: (_: any, row: Contacto) => (
        <span className="text-gray-900 dark:text-gray-100">
          {row.fecha
            ? new Date(row.fecha).toLocaleString('es-AR', {
                dateStyle: 'short',
                timeStyle: 'short',
              })
            : '—'}
        </span>
      ),
    },
    {
      key: 'tipoDeContactoNombre',
      label: 'Tipo',
      render: (_: any, row: Contacto) => (
        <span className="text-gray-900 dark:text-gray-100">
          {row.tipoDeContactoNombre ||
            TIPO_CONTACTO_OPTIONS.find((t) => t.id === row.tipoDeContacto)?.nombre ||
            '—'}
        </span>
      ),
    },
    {
      key: 'clienteNombre',
      label: 'Cliente',
      render: (_: any, row: Contacto) => (
        <span className="text-gray-900 dark:text-gray-100">{row.clienteNombre || '—'}</span>
      ),
    },
    {
      key: 'sucursalNombre',
      label: 'Sucursal',
      render: (_: any, row: Contacto) => (
        <span className="text-gray-900 dark:text-gray-100">{row.sucursalNombre || '—'}</span>
      ),
    },
    {
      key: 'usuarioNombre',
      label: 'Usuario',
      render: (_: any, row: Contacto) => (
        <span className="text-gray-900 dark:text-gray-100">{row.usuarioNombre || '—'}</span>
      ),
    },
    {
      key: 'detalles',
      label: 'Detalles',
      render: (_: any, row: Contacto) => (
        <span className="text-gray-700 dark:text-gray-300 truncate max-w-xs block">
          {row.detalles || '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Agenda de Contactos</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Listado de contactos comerciales
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate('/agenda-contactos')}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
          >
            Agenda
          </button>
          <button
            type="button"
            onClick={() => navigate('/contactos/nuevo')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
          >
            <PlusIcon className="w-4 h-4" />
            Nuevo contacto
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Cliente
          </label>
          <select
            value={clienteId}
            onChange={async (e) => {
              const v = e.target.value === '' ? '' : Number(e.target.value);
              setClienteId(v);
              setSucursalId('');
              await loadSucursalesForCliente(v === '' ? undefined : v);
            }}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          >
            <option value="">Todos</option>
            {clienteOptions.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Sucursal
          </label>
          <select
            value={sucursalId}
            onChange={(e) => setSucursalId(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          >
            <option value="">Todas</option>
            {sucursalOptions.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Desde
          </label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Hasta
          </label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={load}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Buscar
          </button>
        </div>
      </div>

      <DataTable
        data={contactos}
        columns={columns}
        loading={loading}
        enableAdvancedFilters={false}
        searchPlaceholder="Buscar…"
        emptyMessage="No hay contactos"
        onView={(row) => navigate(`/contactos/${row.id}`)}
        onEdit={(row) => navigate(`/contactos/${row.id}/editar`)}
        onDelete={handleDelete}
        actions={[
          {
            label: 'Ver',
            icon: EyeIcon,
            onClick: (row) => navigate(`/contactos/${row.id}`),
            className: 'text-gray-400 hover:text-blue-500',
          },
          {
            label: 'Editar',
            icon: PencilIcon,
            onClick: (row) => navigate(`/contactos/${row.id}/editar`),
            className: 'text-gray-400 hover:text-yellow-500',
          },
          {
            label: 'Eliminar',
            icon: TrashIcon,
            onClick: handleDelete,
            className: 'text-gray-400 hover:text-red-500',
          },
        ]}
      />
    </div>
  );
};

export default ContactosPage;
